import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '../../../../components/Modal/modal';
import {
    setClinicalApprovementTreatmentPlanData,
    setIsClinicalSmulationApproveTreatmentPlan,
    setPlanId,
} from '../../../../store/SmileDesign/SmileDesignSlice';
import { FormControlLabel, Grid, Radio, RadioGroup } from '@material-ui/core';
import RadioRhf from '../../../../components/ReactHookForm/RadioRhf/RadioRhf';
import { RadioOptionRhf } from '../../../../components/ReactHookForm/RadioRhf/RadioOptionRhf';
import { FormProvider, Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import RestorativePhaseButton from '../../RestorativePhaseButton/RestorativePhaseButton';
import instance, { safeGet, safePost } from '../../../../axios-default';
import { easySnackbar } from '../../../../store/Noti/noti.actions';
import { useHistory } from 'react-router-dom'; // ignore the error if error is 'Module '"react-router-dom"' has no exported member 'useHistory'
import PercentageProgressRing from '../../../../components/PercentageProgressRing';
import { useLang } from '../../../../util/lang';
import UpgradeTreatmentPlanModal from '../UpgradeTreatmentPlanModal/UpgradeTreatmentPlanModal';
import { ClinicalApprovementOption } from '../../clinicalApprovementOptionConstants';
import {
    setApprovementOption,
    UPSELL_TYPE,
} from '../../../../store/TreatmentPlanApproval/TreatmentPlanApprovalSlice';
import RestorationFormFields, {
    RESTORATION_FORM_DEFAULT_VALUES,
    RESTORATION_FORM_FIELD_NAMES,
} from '../../RestorationFormFields';

const validationSchema = yup.object({
    clinicalApprovementTreatmentPlan: yup.string().required('This field is required'),
    restorationType: yup.string().when('clinicalApprovementTreatmentPlan', {
        is: ClinicalApprovementOption[2],
        then: () => yup.string().required('This field is required'),
    }),
    biofunctionalMockup: yup.string().when('clinicalApprovementTreatmentPlan', {
        is: ClinicalApprovementOption[2],
        then: () => yup.string().required('This field is required'),
    }),
    materialShade: yup.string().when(['clinicalApprovementTreatmentPlan', 'biofunctionalMockup'], {
        is: (approvalOption, mockupOption) =>
            approvalOption === ClinicalApprovementOption[2] && mockupOption === 'mockupKeyAndMaterial',
        then: () => yup.string().required('This field is required'),
    }),
    gingivectomyGuide: yup.string().when('clinicalApprovementTreatmentPlan', {
        is: ClinicalApprovementOption[2],
        then: () => yup.string().required('This field is required'),
    }),
});

const validationSchemaWithoutRestoration = yup.object({
    clinicalApprovementTreatmentPlan: yup.string().required('This field is required'),
});

const DEFAULT_FORM_VALUES = {
    clinicalApprovementTreatmentPlan: '',
    ...RESTORATION_FORM_DEFAULT_VALUES,
};
const udpateStatusApi = async (caseId, planId, approvementOption) => {
    const result = await safePost(`/case/smileDesign/approveIDesignAligner/${caseId}/${planId}`, {
        approvementOption: approvementOption,
    });
    return result;
};

/**
 * Renders the Smile Design treatment approval modal.
 */
const ClinicalApprovementModal = ({ caseId, planId = undefined }) => {
    const get = useLang();
    const dispatch = useDispatch();
    const progressBarRef = useRef(null);
    const history = useHistory();
    const displayFirstFitFlag = useSelector((state) => state.globalReducer.displayFirstFitFlag);
    const isMarieGroup = useMemo(() => JSON.parse(localStorage.getItem('isMarieGroup')), []);
    const methods = useForm({
        resolver: yupResolver(displayFirstFitFlag ? validationSchema : validationSchemaWithoutRestoration),
        mode: 'onChange',
        defaultValues: DEFAULT_FORM_VALUES,
    });
    const isClinicalSmulationApproveTreatmentPlan = useSelector(
        (state) => state.smileDesign.isClinicalSmulationApproveTreatmentPlan
    );
    const planIdinMultiplePlan = useSelector((state) => state.smileDesign.planId);
    const treatmentPlanApproval = useSelector((state) => state.treatmentPlanApproval);
    const { upsellType, shouldApproveUpsellPart } = treatmentPlanApproval;

    // Usage: modal open
    const [openRestorationForm, setOpenRestorationForm] = useState(false);
    const [openUpgradeTreatmentPlanModal, setOpenUpgradeTreatmentPlanModal] = useState(false);

    const [openApprovalChangeConfirm, setOpenApprovalChangeConfirm] = useState(false);
    const [defaultRestorationType, setDefaultRestorationType] = useState('');
    const [pendingRedirectPath, setPendingRedirectPath] = useState('');
    const [pendingRestorativeApproval, setPendingRestorativeApproval] = useState(null);
    const [openRestorativeRedirectConfirm, setOpenRestorativeRedirectConfirm] = useState(false);
    const [hasLoadedPrescriptionNotes, setHasLoadedPrescriptionNotes] = useState(false);

    // Usage: loading
    const [isLoading, setIsLoading] = useState(false);

    const [isDisableSmileDesignOnly, setIsDisableSmilDesignOnly] = useState(true);

    const handleApproveUpsellTreatmentPlan = async (approvementOption) => {
        // Route upsell approval through upgrade or direct approval flow.
        const currentPlanId = planId === undefined ? planIdinMultiplePlan : planId;
        if (shouldApproveUpsellPart && upsellType === UPSELL_TYPE.ALIGN_FIRST_Q16) {
            dispatch(setApprovementOption(approvementOption));
            setOpenUpgradeTreatmentPlanModal(true);
            return;
        }

        const formData = new FormData();
        formData.append('body', JSON.stringify({ upsellApprovementOption: approvementOption }));
        const result = await safePost(`case/${caseId}/treatment_plan/${currentPlanId}/approve`, formData);
        if (result?.success) {
            window.location.reload();
        }
    };

    const formValues = methods.watch();
    const isRestorationApproval = formValues.clinicalApprovementTreatmentPlan === ClinicalApprovementOption[2];
    const hasDependentRestorationAnswers = Boolean(
        formValues.biofunctionalMockup ||
            formValues.materialShade ||
            formValues.gingivectomyGuide ||
            formValues.restorationRemark
    );
    const hasRestorationAnswers = Boolean(
        (formValues.restorationType && formValues.restorationType !== defaultRestorationType) ||
            hasDependentRestorationAnswers
    );

    /** Clears answers that depend on the selected restoration type. */
    const clearRestorationAnswers = () => {
        RESTORATION_FORM_FIELD_NAMES.slice(1).forEach((fieldName) => {
            methods.setValue(fieldName, '', { shouldValidate: true });
        });
    };

    /** Clears every restoration field before entering the alignment approval flow. */
    const clearAllRestorationAnswers = () => {
        methods.setValue('restorationType', '', { shouldValidate: true });
        clearRestorationAnswers();
    };

    /** Loads the Prescription Note Q1 value when restorative approval is selected. */
    const loadPrescriptionNotes = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await safeGet(`/case/smileDesignPrescriptionNotes/${caseId}`);
            const restorationType = response?.result?.plannedRestorationSolution || '';
            setDefaultRestorationType(restorationType);
            methods.setValue('restorationType', restorationType, { shouldValidate: true });
            await methods.trigger();
            setHasLoadedPrescriptionNotes(true);
        } finally {
            setIsLoading(false);
        }
    }, [caseId, methods]);

    useEffect(() => {
        const isRestorativeApproval = formValues.clinicalApprovementTreatmentPlan === ClinicalApprovementOption[2];
        if (isClinicalSmulationApproveTreatmentPlan && isRestorativeApproval && !hasLoadedPrescriptionNotes) {
            loadPrescriptionNotes();
        } else if ((!isClinicalSmulationApproveTreatmentPlan || !isRestorativeApproval) && hasLoadedPrescriptionNotes) {
            setHasLoadedPrescriptionNotes(false);
        }
    }, [
        formValues.clinicalApprovementTreatmentPlan,
        hasLoadedPrescriptionNotes,
        isClinicalSmulationApproveTreatmentPlan,
        loadPrescriptionNotes,
    ]);

    /** Requests confirmation before discarding restoration answers for alignment approval. */
    const handleApprovalOptionChange = (approvalOption) => {
        if (
            approvalOption === ClinicalApprovementOption[1] &&
            formValues.clinicalApprovementTreatmentPlan === ClinicalApprovementOption[2] &&
            hasRestorationAnswers
        ) {
            setOpenApprovalChangeConfirm(true);
            return;
        }

        methods.setValue('clinicalApprovementTreatmentPlan', approvalOption, { shouldValidate: true });
        methods.trigger();
    };

    /** Navigates after the approval dialog has finished releasing its scroll lock. */
    const handleApprovalDialogExited = () => {
        if (!pendingRedirectPath) return;

        setPendingRedirectPath('');
        history.replace(pendingRedirectPath);
    };

    const submitSmileDesignRestorativeApproval = async (data, currentPlanId) => {
        const formData = new FormData();
        formData.append(
            'body',
            JSON.stringify({
                approvementOption: data.clinicalApprovementTreatmentPlan,
            })
        );
        setIsLoading(true);
        const result = await instance.post(
            `case/restorationForm/approveSmileDesignPlan/${caseId}/${currentPlanId}`,
            formData,
            { onUploadProgress: progressBarRef.current?.onProgressChange }
        );

        if (result.data.success) {
            const shouldRedirectToRestorativePhase = data.biofunctionalMockup === 'no';
            const shouldCreateMockupOrder = data.biofunctionalMockup !== 'no' || data.gingivectomyGuide === 'yes';

            if (shouldCreateMockupOrder) {
                const mockupOrderResult = await safePost(`case/mockup_order/${caseId}`, {
                    restorationType: data.restorationType,
                    biofunctionalMockupOrder: `option.${data.biofunctionalMockup}`,
                    materialShade: data.materialShade,
                    gingivectomyGuide: `option.${data.gingivectomyGuide}`,
                    remark: data.restorationRemark,
                });

                if (!mockupOrderResult?.success) {
                    dispatch(easySnackbar(mockupOrderResult?.message || 'Failed to submit mock-up order', 'error'));
                    return;
                }
            }

            dispatch(easySnackbar(`${get('@PatientInfo.smileDesign.approveSmileDesignPlan')}`, 'success', true));
            if (shouldCreateMockupOrder) {
                dispatch(easySnackbar(`${get('@PatientInfo.smileDesign.submitMockupOrder')}`, 'success', true));
            }
            setPendingRedirectPath(
                shouldRedirectToRestorativePhase
                    ? `/patients/${caseId}/restorative-phase-case-intake`
                    : `/patients/${caseId}`
            );
            dispatch(setIsClinicalSmulationApproveTreatmentPlan(false));
        }
    };

    const handleConfirmRestorativeRedirect = async () => {
        const approval = pendingRestorativeApproval;

        setOpenRestorativeRedirectConfirm(false);
        setPendingRestorativeApproval(null);
        if (!approval) return;

        try {
            await submitSmileDesignRestorativeApproval(approval.data, approval.currentPlanId);
        } catch (err) {
            dispatch(easySnackbar(err.response.data.message, 'error'));
        } finally {
            setIsLoading(false);
        }
    };

    /** Submits the selected approval path without changing the existing endpoint behavior. */
    const onSubmit = async (data) => {
        console.log('onSubmit data: ', data);
        if(displayFirstFitFlag){
            let currentPlanId = planId === undefined ? planIdinMultiplePlan : planId;
            try {
                if (upsellType) {
                    dispatch(setIsClinicalSmulationApproveTreatmentPlan(false));
                    await handleApproveUpsellTreatmentPlan(data?.clinicalApprovementTreatmentPlan);
                    return;
                }
                switch (data?.clinicalApprovementTreatmentPlan) {
                    case ClinicalApprovementOption[1]: {
                        dispatch(setIsClinicalSmulationApproveTreatmentPlan(false));
                        setOpenUpgradeTreatmentPlanModal(true);
                        break;
                    }
                    case ClinicalApprovementOption[2]: {
                        if (data.biofunctionalMockup === 'no') {
                            setPendingRestorativeApproval({ data: { ...data }, currentPlanId });
                            setOpenRestorativeRedirectConfirm(true);
                            break;
                        }

                        await submitSmileDesignRestorativeApproval(data, currentPlanId);
                        break;
                    }
                }
            } catch (err) {
                dispatch(easySnackbar(err.response.data.message, 'error'));
            } finally {
                setIsLoading(false);
            }
        }else{
            dispatch(setIsClinicalSmulationApproveTreatmentPlan(false)); // Purpose: remove the first dialog under second dialog
            let currentPlanId = planId === undefined ? planIdinMultiplePlan : planId;
            try {

                if (upsellType) {
                    await handleApproveUpsellTreatmentPlan(data?.clinicalApprovementTreatmentPlan);
                    return;
                }
                // setIsLoading(true)
                switch (data?.clinicalApprovementTreatmentPlan) {
                    case ClinicalApprovementOption[1]: {
                        setOpenUpgradeTreatmentPlanModal(true);
                        break;
                    }
                    case ClinicalApprovementOption[2]: {
                        dispatch(setClinicalApprovementTreatmentPlanData(data?.clinicalApprovementTreatmentPlan)); // Pupose: inform restorationForm the save function that the api will be changed.
                        setOpenRestorationForm(true);
                        break;
                    }
                }
            } catch (err) {
                dispatch(easySnackbar(err.response.data.message, 'error'));
            } finally {
                // setIsLoading(false)
            }
        }
        
    };

    useEffect(() => {
        async function getIsOnlySmileDesign() {
            if (isClinicalSmulationApproveTreatmentPlan && caseId) {
                const allowedUpgradeResponse = await instance.get(`/case/smileDesign/allowedUpgrade/${caseId}`);
                const data = allowedUpgradeResponse.data.result;
                if (!!data?.comprehensive && !!data?.twelveStep) {
                    setIsDisableSmilDesignOnly(true);
                } else {
                    setIsDisableSmilDesignOnly(false);
                }
            }
        }

        getIsOnlySmileDesign();
    }, [isClinicalSmulationApproveTreatmentPlan]);

    const option2 = useMemo(() => {
        if (upsellType === UPSELL_TYPE.ALIGN_FIRST_Q16) {
            return {
                label: get('@PatientInfo.smileDesign.clinicalApprovement.option.approveSmileDesignUpsell'),
                value: ClinicalApprovementOption.APPROVE_IDESIGN_ALIGNER,
            };
        }
        return {
            label: get('@PatientInfo.smileDesign.clinicalApprovement.option.approveSmileDesign'),
            value: ClinicalApprovementOption[2],
        };
    }, [get, upsellType]);

    return (
        <FormProvider {...methods}>
            <Modal
                className="prescriptionNotes"
                modal="prescriptionNotes"
                title={''}
                handleClose={() => dispatch(setIsClinicalSmulationApproveTreatmentPlan(false))}
                open={isClinicalSmulationApproveTreatmentPlan}
                submitButton={true}
                modalError={false}
                onExited={handleApprovalDialogExited}
                handleDataSave={methods.handleSubmit(onSubmit)}
                saveLabel={displayFirstFitFlag ? ':const.Proceed' : ':const.save'}
                submitDisabled={(displayFirstFitFlag ? !methods.formState.isValid : false)}
            >
                <div className={displayFirstFitFlag ? 'isd-prescription-radio-scope' : undefined}>
                    <Grid container direction="column">
                        {displayFirstFitFlag ? 
                            <>
                                <Controller
                                    control={methods.control}
                                    name="clinicalApprovementTreatmentPlan"
                                    render={({ field }) => (
                                        <RadioGroup value={field.value} onChange={(event) => handleApprovalOptionChange(event.target.value)}>
                                            <FormControlLabel
                                                value={ClinicalApprovementOption[1]}
                                                control={<Radio />}
                                                label={get('@PatientInfo.smileDesign.clinicalApprovement.option.approveiDesign2AndSmileDesign')}
                                                disabled={isDisableSmileDesignOnly}
                                            />
                                            <FormControlLabel value={option2.value} control={<Radio />} label={option2.label} />
                                        </RadioGroup>
                                    )}
                                />
                                {isRestorationApproval && (
                                    <Grid item style={{ marginTop: 16 }}>
                                        <RestorationFormFields isMarieGroup={isMarieGroup} />
                                    </Grid>
                                )}
                            </>
                        :<RadioRhf
                                key={'clinicalApprovementTreatmentPlan'}
                                question={''}
                                name={'clinicalApprovementTreatmentPlan'}
                                formControlLabelRhf={
                                    <>
                                        <RadioOptionRhf
                                            value={ClinicalApprovementOption[1]}
                                            label={get(
                                                '@PatientInfo.smileDesign.clinicalApprovement.option.approveiDesign2AndSmileDesign'
                                            )}
                                            disable={isDisableSmileDesignOnly}
                                        />

                                        <RadioOptionRhf value={option2.value} label={option2.label} />
                                    </>
                                }
                            />}
                        
                    </Grid>
                </div>
            </Modal>
            <PercentageProgressRing ref={progressBarRef} open={isLoading} label={undefined} />
            
            {/* Usage: Modal */}
            <RestorativePhaseButton
                caseId={caseId}
                planId={planId}
                isShowButton={false}
                isOpenExternal={openRestorationForm}
                setIsOpenExternal={setOpenRestorationForm}
            />

            <Modal
                title=""
                open={openApprovalChangeConfirm}
                maxWidth="sm"
                handleClose={() => setOpenApprovalChangeConfirm(false)}
                submitButton={true}
                submitButtonLabel="Continue"
                handleDataSave={() => {
                    clearAllRestorationAnswers();
                    methods.setValue('clinicalApprovementTreatmentPlan', ClinicalApprovementOption[1], { shouldValidate: true });
                    methods.trigger();
                    setOpenApprovalChangeConfirm(false);
                }}
            >
                Entering the TrioClear alignment phase removes the restoration answers below. Continue?
            </Modal>

            <Modal
                title=""
                open={openRestorativeRedirectConfirm}
                maxWidth="sm"
                submitButton={true}
                submitButtonLabel={get(':const.ok')}
                handleDataSave={handleConfirmRestorativeRedirect}
            >
                <div
                    dangerouslySetInnerHTML={{
                        __html: get('@PatientInfo.smileDesign.restorativeRedirectNotice'),
                    }}
                />
            </Modal>

            {/* Usage: Modal */}
            <UpgradeTreatmentPlanModal
                openExtenal={openUpgradeTreatmentPlanModal}
                setOpenExtenal={setOpenUpgradeTreatmentPlanModal}
                planId={planId}
                caseId={caseId}
            />
        </FormProvider>
    );
};

export default memo(ClinicalApprovementModal);
